/**
 * rehype plugin: a leading emoji that marks a kind of block becomes a Font
 * Awesome icon - after rehypeSafeHtml, which strips the classes an author's
 * HTML could carry; only these, set here, get through.
 *
 * - A quote starting with ℹ️, ⚠️, ⛔, ✅, 📝, ❓ or ☑️ (the wiki's "Box"
 *   templates, as the MediaWiki import writes them) is a callout: its icon in
 *   front, and a class for the colour of its kind.
 * - Bold text starting with ▶️ (a video's title, from the same import) gets a
 *   play icon.
 *
 * The emoji stays in the stored text, where it still reads right wherever the
 * article is not rendered (search results, an export).
 */

export const CALLOUTS = {
  'ℹ': ['info', 'fa-circle-info'],
  '⚠': ['warning', 'fa-triangle-exclamation'],
  '⛔': ['danger', 'fa-circle-exclamation'],
  '✅': ['success', 'fa-circle-check'],
  '📝': ['note', 'fa-pen-to-square'],
  '❓': ['question', 'fa-circle-question'],
  '☑': ['todo', 'fa-square-check'],
};

const INLINE = {
  '▶': 'fa-circle-play',
};

// The emoji, its optional variation selector (U+FE0F) and the space after it.
export const LEADING = /^\s*(\p{Extended_Pictographic})️?\s*/u;

function icon(name, extra) {
  return {
    type: 'element',
    tagName: 'i',
    properties: { className: ['fa-solid', name, extra], ariaHidden: 'true' },
    children: [],
  };
}

const firstElement = (node) => node.children?.find((child) => child.type === 'element');

/** Strips a leading emoji from `node`'s first text and returns it, or null. */
function takeLeadingEmoji(node, known) {
  const text = node.children?.[0];
  if (text?.type !== 'text') return null;

  const match = text.value.match(LEADING);
  if (!match || !(match[1] in known)) return null;

  text.value = text.value.slice(match[0].length);
  return match[1];
}

function visit(node) {
  if (node.type === 'element' && node.tagName === 'blockquote') {
    const paragraph = firstElement(node);
    const emoji = paragraph && takeLeadingEmoji(paragraph, CALLOUTS);

    if (emoji) {
      const [kind, name] = CALLOUTS[emoji];
      node.properties = {
        ...node.properties,
        className: ['article-callout', `article-callout--${kind}`],
      };
      paragraph.children.unshift(icon(name, 'article-callout-icon'));
    }
  }

  if (node.type === 'element' && node.tagName === 'strong') {
    const emoji = takeLeadingEmoji(node, INLINE);
    if (emoji) node.children.unshift(icon(INLINE[emoji], 'article-inline-icon'));
  }

  for (const child of node.children ?? []) visit(child);
}

export default function rehypeEmojiIcons() {
  return (tree) => {
    visit(tree);
  };
}
